import { AppResources, AppRoutes } from "@/constants";
import {
  Flex,
  Image,
  Stack,
  Text,
  Title,
  Paper,
  Button,
  Loader,
  Group,
  Divider,
  ScrollArea,
  Accordion,
} from "@mantine/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  getFailedInvoice,
  createPaymentUpdateSetupIntent,
  submitPaymentMethodAndRetryInvoice,
} from "@/utils/billing/api";
import type { FailedInvoiceInfo } from "@/utils/billing/types";
import { IconReceipt, IconHome } from "@tabler/icons-react";
import { useAuthStore } from "@/utils/stores/authStore";

const PAYMENT_ELEMENT_APPEARANCE = {
  theme: "flat" as const,
  variables: { colorPrimaryText: "#262626" },
};

interface FailedPaymentProps {
  email?: string;
}

/** In-page form: confirm SetupIntent then call set-default-and-retry. Must be rendered inside Elements. */
const PaymentUpdateForm: React.FC<{
  clientSecret: string;
  onSuccess: () => void;
}> = ({ clientSecret, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const paymentMethodIdFromSetupIntent = useCallback(
    (setupIntent: {
      payment_method?: string | { id?: string | null } | null;
    }) => {
      const paymentMethod = setupIntent.payment_method;
      return typeof paymentMethod === "string"
        ? paymentMethod
        : (paymentMethod?.id ?? null);
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements || submitting) return;
      try {
        setSubmitting(true);
        let paymentMethodId: string | null = null;

        const existingIntentResult =
          await stripe.retrieveSetupIntent(clientSecret);
        if (existingIntentResult.setupIntent?.status === "succeeded") {
          paymentMethodId = paymentMethodIdFromSetupIntent(
            existingIntentResult.setupIntent
          );
        }

        if (!paymentMethodId) {
          // Required: submit and validate the Payment Element before confirmSetup.
          const { error: submitError } = await elements.submit();
          if (submitError) {
            notifications.show({
              title: "Validation error",
              message:
                submitError.message ?? "Please check your payment details.",
              color: "red",
            });
            return;
          }
          const confirmResult = await stripe.confirmSetup({
            elements,
            clientSecret,
            confirmParams: {
              return_url: window.location.origin + AppRoutes.paymentUpdate,
            },
            redirect: "if_required",
          });
          if (confirmResult.error) {
            if (confirmResult.error.code === "setup_intent_unexpected_state") {
              const retryIntentResult =
                await stripe.retrieveSetupIntent(clientSecret);
              if (retryIntentResult.setupIntent?.status === "succeeded") {
                paymentMethodId = paymentMethodIdFromSetupIntent(
                  retryIntentResult.setupIntent
                );
              }
            }
            if (!paymentMethodId) {
              notifications.show({
                title: "Payment method error",
                message:
                  confirmResult.error.message ??
                  "Could not confirm payment method.",
                color: "red",
              });
              return;
            }
          } else {
            paymentMethodId = confirmResult.setupIntent
              ? paymentMethodIdFromSetupIntent(confirmResult.setupIntent)
              : null;
          }
        }

        if (!paymentMethodId) {
          notifications.show({
            title: "Error",
            message: "No payment method received.",
            color: "red",
          });
          return;
        }
        const apiResult =
          await submitPaymentMethodAndRetryInvoice(paymentMethodId);
        if (apiResult.paymentSuccessful) {
          notifications.show({
            title: "Payment method updated",
            message:
              apiResult.paidCount !== undefined && apiResult.paidCount > 0
                ? `We've updated your payment method and charged ${apiResult.paidCount} invoice(s).`
                : "Your payment method has been updated.",
            color: "green",
          });
          onSuccess();
        } else {
          notifications.show({
            title: "Update completed",
            message:
              apiResult.failedCount && apiResult.failedCount > 0
                ? "Payment method saved, but we couldn't charge one or more invoices. You can try again from your account."
                : "Payment method saved.",
            color: "orange",
          });
          onSuccess();
        }
      } catch (err) {
        console.error("Submit payment update error:", err);
        notifications.show({
          title: "Error",
          message:
            "Payment method could not be saved or we couldn't charge the invoice. Please try again.",
          color: "red",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      stripe,
      elements,
      clientSecret,
      onSuccess,
      paymentMethodIdFromSetupIntent,
      submitting,
    ]
  );

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <PaymentElement options={{ paymentMethodOrder: ["card"] }} />
        <Button
          type="submit"
          size="lg"
          color="#0077CC"
          fullWidth
          loading={submitting}
          disabled={!stripe || !elements}
        >
          Update payment method
        </Button>
      </Stack>
    </form>
  );
};

const FailedPayment: React.FC<FailedPaymentProps> = () => {
  const { user, loading: authLoading, isOwner, refreshAuth } = useAuthStore();
  const [invoice, setInvoice] = useState<FailedInvoiceInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noInvoiceFound, setNoInvoiceFound] = useState(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<
    string | null
  >(null);
  const [setupIntentError, setSetupIntentError] = useState<string | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);

  /**
   * Refresh auth state on mount to get the latest user data after login.
   * This is needed because the auth store may have stale data from before login
   * when the component is rendered directly without a route change.
   */
  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  /**
   * Check if the current user is authenticated and is an owner.
   * Only authenticated owners should see payment update options; team members see a contact owner message.
   * Wait for auth to complete before determining role.
   */
  const userIsOwner = !authLoading && !!user && isOwner();

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const data = await getFailedInvoice();
        setInvoice(data);
      } catch (err: unknown) {
        console.error("Failed to fetch invoice:", err);
        // Check if it's a 404 (no invoice found)
        if (
          err &&
          typeof err === "object" &&
          "response" in err &&
          (err as { response?: { status?: number } }).response?.status === 404
        ) {
          setNoInvoiceFound(true);
        } else {
          setError("Could not load invoice details");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, []);

  /**
   * Create SetupIntent when owner has an invoice (payment-update flow).
   * Used to mount Stripe Elements and PaymentElement.
   */
  useEffect(() => {
    if (!invoice || noInvoiceFound) return;
    let cancelled = false;
    const create = async () => {
      setSetupIntentError(null);
      try {
        const { clientSecret, publishableKey: key } =
          await createPaymentUpdateSetupIntent();
        if (!cancelled) {
          setSetupIntentClientSecret(clientSecret);
          setPublishableKey(key);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        console.error("Create setup intent error:", err);
        const status =
          err &&
          typeof err === "object" &&
          "response" in err &&
          (err as { response?: { status?: number } }).response?.status;
        setSetupIntentError(
          status === 404 || status === 400
            ? "Payment update is not required or no longer available."
            : "Could not load payment form. Please try again."
        );
      }
    };
    create();
    return () => {
      cancelled = true;
    };
  }, [invoice, noInvoiceFound]);

  /**
   * Format cents to a currency string.
   */
  const formatCurrency = (cents: number, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  /**
   * Format a Unix timestamp to a readable date string.
   */
  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date(timestamp * 1000));
  };

  /**
   * Navigate to home page with full reload (after successful payment update).
   */
  const handleGoHome = useCallback(() => {
    window.location.href = AppRoutes.home;
  }, []);

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  );

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <Stack gap="xl" p="md" h="100%" justify="center" align="center">
          <Loader size="lg" />
          <Text c="dimmed">Loading...</Text>
        </Stack>
      </div>
    );
  }

  // Non-owner view: Show message to contact account owner
  if (!userIsOwner) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <Stack gap="xl" p="md" h="100%" justify="center" align="center">
          <Flex justify="center">
            <Image
              src={AppResources.invalidPayment}
              alt="Payment issue"
              maw={{ base: 150, sm: 200, md: 220 }}
              mah={220}
              fit="contain"
            />
          </Flex>
          <Stack gap="sm" ta="center" maw={500}>
            <Title order={3} fw={700} c="orange">
              Payment Update Required
            </Title>
            <Text c="dimmed">
              There's an issue with your organization's subscription payment.
              Please contact your account owner to resolve this matter.
            </Text>
            <Text size="sm" c="dimmed" mt="md">
              Once the issue is resolved, you'll be able to access all features
              again.
            </Text>
          </Stack>
        </Stack>
      </div>
    );
  }

  // Owner view: Show payment update options
  return (
    <div style={{ height: "100vh", width: "100vw" }}>
      <Stack gap="xl" p="md" h="100%" my="auto">
        {/* Main content */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          w="100%"
          maw={1200}
          m="auto"
          gap="xl"
          justify="center"
          align="center"
        >
          {/* Image section */}
          <Stack justify="center" w={{ base: "100%", sm: "40%" }}>
            <Flex justify="center">
              <Image
                src={AppResources.invalidPayment}
                alt="Payment failed"
                maw={{ base: 150, sm: 200, md: 220 }}
                mah={220}
                fit="contain"
              />
            </Flex>

            {/* Text content */}
            <Stack gap="sm" ta="center">
              <Title order={3} fw={700}>
                {noInvoiceFound
                  ? "No Outstanding Payments"
                  : "We couldn't process your payment"}
              </Title>
              <Text maw={600} mx="auto">
                {noInvoiceFound
                  ? "You don't have any outstanding invoices. You're all set!"
                  : "Your payment didn't go through. To prevent any interruption, please pay the outstanding invoice."}
              </Text>
              <Text c="dimmed" fz="sm" maw={600} mx="auto">
                If you have any questions, please contact support at{" "}
                <Text span c="#0077CC" fw={600} fz="sm">
                  help@smartwiz.io
                </Text>
              </Text>
            </Stack>

            {/* Go Home Button - only show when no invoice found */}
            {noInvoiceFound && (
              <Flex justify="center" mt="md">
                <Button
                  size="lg"
                  color="#0077CC"
                  onClick={handleGoHome}
                  leftSection={<IconHome size={18} />}
                >
                  Go to Home
                </Button>
              </Flex>
            )}
          </Stack>

          {/* Invoice Panel - only show when there's an invoice or loading/error */}
          {!noInvoiceFound && (
            <Flex justify="center" w={{ base: "100%", sm: "60%" }}>
              <div style={{ width: "100%" }}>
                <Paper shadow="xl" radius="md" p="xl" mih={425} w="100%">
                  {loading ? (
                    <Stack align="center" justify="center" h="100%">
                      <Loader size="lg" />
                      <Text>Loading invoice details...</Text>
                    </Stack>
                  ) : error ? (
                    <Stack align="center" justify="center" h="100%">
                      <Text c="red">{error}</Text>
                    </Stack>
                  ) : invoice ? (
                    <ScrollArea.Autosize
                      mah="90vh"
                      scrollbars="y"
                      offsetScrollbars={true}
                    >
                      <Stack gap="md" p="md">
                        <Group justify="space-between" align="center">
                          <Group gap="xs">
                            <IconReceipt size={24} color="#0077CC" />
                            <Title fw={600} order={3}>
                              Payment Details
                            </Title>
                          </Group>
                        </Group>

                        <Divider />
                        <Accordion>
                          {/* Invoice Details */}
                          <Accordion.Item value="invoice-details">
                            <Accordion.Control>
                              <Text size="sm" fw={500}>
                                Invoice Details
                              </Text>
                            </Accordion.Control>
                            <Accordion.Panel>
                              <Stack gap="md">
                                <Stack gap="xs">
                                  <Group justify="space-between">
                                    <Text size="sm" c="dimmed">
                                      Invoice Date
                                    </Text>
                                    <Text size="sm" fw={500}>
                                      {formatDate(invoice.created)}
                                    </Text>
                                  </Group>

                                  {invoice.dueDate && (
                                    <Group justify="space-between">
                                      <Text size="sm" c="dimmed">
                                        Due Date
                                      </Text>
                                      <Text size="sm" fw={500}>
                                        {formatDate(invoice.dueDate)}
                                      </Text>
                                    </Group>
                                  )}

                                  <Group justify="space-between">
                                    <Text size="sm" c="dimmed">
                                      Payment Attempts
                                    </Text>
                                    <Text size="sm" fw={500}>
                                      {invoice.attemptCount}
                                    </Text>
                                  </Group>
                                </Stack>

                                <Divider />

                                {/* Line Items - Show first item only, with "more" if multiple */}
                                <Stack gap="xs">
                                  <Text size="sm" fw={600} c="#0077CC">
                                    CHARGES
                                  </Text>
                                  {invoice.lineItems.length > 0 && (
                                    <Group justify="space-between">
                                      <Text size="sm" maw={250} lineClamp={2}>
                                        {invoice.lineItems[0].description}
                                      </Text>
                                      <Text size="sm" fw={500}>
                                        {formatCurrency(
                                          invoice.lineItems[0].amount,
                                          invoice.lineItems[0].currency
                                        )}
                                      </Text>
                                    </Group>
                                  )}
                                  {invoice.lineItems.length > 1 && (
                                    <Text size="sm" c="dimmed">
                                      + {invoice.lineItems.length - 1} more item
                                      {invoice.lineItems.length - 1 > 1
                                        ? "s"
                                        : ""}
                                    </Text>
                                  )}
                                </Stack>
                              </Stack>
                            </Accordion.Panel>
                          </Accordion.Item>
                        </Accordion>

                        {/* Amount Due */}
                        <Group justify="space-between" align="center">
                          <Text size="lg" fw={600}>
                            Amount Due
                          </Text>
                          <Text size="xl" fw={700} c="red">
                            {formatCurrency(
                              invoice.amountRemaining,
                              invoice.currency
                            )}
                          </Text>
                        </Group>

                        {/* In-page Stripe SetupIntent + Payment Element */}
                        {setupIntentError ? (
                          <Text size="sm" c="red" mt="md">
                            {setupIntentError}
                          </Text>
                        ) : setupIntentClientSecret && stripePromise ? (
                          <Elements
                            stripe={stripePromise}
                            options={{
                              clientSecret: setupIntentClientSecret,
                              appearance: PAYMENT_ELEMENT_APPEARANCE,
                            }}
                          >
                            <PaymentUpdateForm
                              clientSecret={setupIntentClientSecret}
                              onSuccess={handleGoHome}
                            />
                          </Elements>
                        ) : (
                          <Stack align="center" gap="xs" mt="md">
                            <Loader size="sm" />
                            <Text size="sm" c="dimmed">
                              Loading payment form...
                            </Text>
                          </Stack>
                        )}

                        {invoice.nextPaymentAttempt && (
                          <Text size="xs" c="dimmed" ta="center">
                            Next automatic retry:{" "}
                            {formatDate(invoice.nextPaymentAttempt)}
                          </Text>
                        )}
                      </Stack>
                    </ScrollArea.Autosize>
                  ) : null}
                </Paper>
              </div>
            </Flex>
          )}
        </Flex>
      </Stack>
    </div>
  );
};

export default FailedPayment;
